import {computed, observable, makeObservable} from 'mobx';
import _ from 'underscore';
import {type IAttributeTemplate, AttributeTemplateObjectType} from '../schema';

import {create} from 'zustand';

interface AttributeStoreState {
	attributes: IAttributeTemplate[];
	loading: boolean;
	systemAttributes: IAttributeTemplate[];
	userAttributes: IAttributeTemplate[];
	getAttributeById: (id: number) => IAttributeTemplate | undefined;
	getAttributeByName: (name: string) => IAttributeTemplate | undefined;
	getAttributesByIds: (
		ids: number[],
		opts?: {
			omitReadOnly?: boolean;
			omitSystem?: boolean;
			objectType?: AttributeTemplateObjectType;
		},
	) => IAttributeTemplate[];
	update: (attributes: IAttributeTemplate[], loading?: boolean) => void;
}

export const useAttributesStore = create<AttributeStoreState>()((set, get) => ({
	attributes: [],
	loading: false,
	systemAttributes: [],
	userAttributes: [],
	update: (attributes: IAttributeTemplate[], loading = false) => {
		set({
			attributes,
			systemAttributes: attributes.filter((attr) => attr.system),
			userAttributes: attributes.filter((attr) => !attr.system),
			loading,
		});
	},
	getAttributeById: (id: number) =>
		get().attributes.find((attr) => attr.attributeid === id),
	getAttributeByName: (name: string) =>
		get().attributes.find((attr) => attr.name === name),
	getAttributesByIds: (ids: number[], opts) => {
		const {omitSystem, objectType, omitReadOnly} = opts || {};
		return ids
			.map((id) => get().getAttributeById(id))
			.filter((attr) => {
				return (
					attr != null &&
					(!attr.system || !omitSystem) &&
					(attr.permission != 'r' || !omitReadOnly) &&
					(!objectType || attr.objectTypes.includes(objectType))
				);
			}) as IAttributeTemplate[];
	},
}));

// TODO: everything that is in the attributes hook should be moved here and that will use this
// TODO: should think about moving to zustand instead of mobx
export class AttributesStore {
	attributes = observable.array<IAttributeTemplate>(
		useAttributesStore.getState().attributes.map((attr) => ({...attr})),
	);

	_unsubscribers = [] as (() => void)[];

	constructor() {
		makeObservable(
			this,
			{
				attributes: observable,
				systemAttributes: computed,
				userAttributes: computed,
			},
			{
				deep: true,
			},
		);

		this._unsubscribers.push(
			useAttributesStore.subscribe((state) => {
				this.attributes.replace(state.attributes.map((attr) => ({...attr})));
			}),
		);
	}

	get systemAttributes() {
		return this.attributes.filter((attr) => attr.system);
	}

	get userAttributes() {
		return this.attributes.filter((attr) => !attr.system);
	}

	getAttributeById(id: number) {
		return this.attributes.find((attr) => attr.attributeid === id);
	}

	getAttributeByName(name: string) {
		return this.attributes.find((attr) => attr.name === name);
	}

	getAttributesByIds(
		ids: number[],
		opts?: {
			omitReadOnly?: boolean;
			omitSystem?: boolean;
			objectType?: AttributeTemplateObjectType;
		},
	) {
		const {omitSystem, objectType, omitReadOnly} = opts || {};

		return ids
			.map((id) => this.getAttributeById(id))
			.filter((attr) => {
				return (
					attr != null &&
					(!attr.system || !omitSystem) &&
					(attr.permission != 'r' || !omitReadOnly) &&
					(!objectType || attr.objectTypes.includes(objectType))
				);
			}) as IAttributeTemplate[];
	}

	dispose() {
		this._unsubscribers.forEach((unsub) => unsub());
	}
}

export const attributesStore = new AttributesStore();

export default attributesStore;
